import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import StoryInvitePanel from "@/components/StoryInvitePanel";
import ShareCardButton from "@/components/ShareCardButton";

const EMPTY = { first_name: "", quote: "", outcome: "", avatar_url: "", published: false };

function StoryRow({ s, onToggle, onDelete }) {
  return (
    <li className="border border-obsidian-600 p-5 flex gap-4 items-start" data-testid={`admin-story-${s.id}`}>
      <div className="flex-1">
        <p className="font-serif italic text-bone-200">&ldquo;{s.quote}&rdquo;</p>
        {s.source === "invite" && <p className="mt-1 font-sans text-[0.6rem] uppercase tracking-[0.2em] text-terracotta">From invite · permission given</p>}
        <p className="mt-2 font-sans text-xs uppercase tracking-[0.2em] text-bone-500">
          {s.first_name}{s.outcome ? ` · ${s.outcome}` : ""}
        </p>
        {s.published && <ShareCardButton id={s.id} />}
      </div>
      <label className="flex items-center gap-2 font-sans text-xs text-bone-400">
        <Switch checked={s.published} onCheckedChange={() => onToggle(s)} data-testid={`admin-story-publish-${s.id}`} />
        Live
      </label>
      <button onClick={() => onDelete(s)} aria-label="Delete story" data-testid={`admin-story-delete-${s.id}`} className="text-bone-500 hover:text-terracotta">
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  );
}

export default function AdminStories() {
  const [stories, setStories] = useState([]);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/listener-stories").then((r) => setStories(r.data.stories));
  useEffect(() => { load(); }, []);

  const clean = (s) => ({ ...s, outcome: s.outcome || null, avatar_url: s.avatar_url || null });
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/listener-stories", clean(form));
      setForm(EMPTY);
      toast.success("Story added.");
      load();
    } catch {
      toast.error("Could not add the story.");
    }
  };
  const toggle = async (s) => {
    const { id, first_name, quote, outcome, avatar_url } = s;
    await api.put(`/admin/listener-stories/${id}`, clean({ first_name, quote, outcome, avatar_url, published: !s.published }));
    load();
  };
  const remove = async (s) => {
    await api.delete(`/admin/listener-stories/${s.id}`);
    load();
  };
  const field = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  const inputCls = "bg-obsidian-800 border-obsidian-600 rounded-none text-bone-100";

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 pt-16 pb-24">
      <Link to="/admin" className="font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta">← Admin</Link>
      <h1 className="mt-6 font-serif text-4xl text-bone-100 mb-3">Listener stories</h1>
      <p className="font-sans text-sm text-bone-400 mb-10">
        Real quotes only, with the listener's permission. The homepage section stays hidden until at least one story is Live.
      </p>
      <StoryInvitePanel />
      <form onSubmit={create} className="space-y-3 mb-12" data-testid="admin-story-form">
        <Input placeholder="First name" required maxLength={40} className={inputCls} data-testid="admin-story-name" {...field("first_name")} />
        <Input placeholder='Quote, e.g. "I finished Chapter Three by speaking for ten minutes at night"' required maxLength={280} className={inputCls} data-testid="admin-story-quote" {...field("quote")} />
        <Input placeholder="Outcome tag (optional), e.g. Finished Chapter Three" maxLength={120} className={inputCls} data-testid="admin-story-outcome" {...field("outcome")} />
        <Input placeholder="Avatar image URL (optional)" maxLength={500} className={inputCls} data-testid="admin-story-avatar" {...field("avatar_url")} />
        <Button type="submit" data-testid="admin-story-submit" className="rounded-none bg-terracotta hover:bg-terracotta-deep text-obsidian-900 uppercase tracking-wider text-xs">
          Add story (hidden until Live)
        </Button>
      </form>
      <ul className="space-y-3" data-testid="admin-story-list">
        {stories.map((s) => <StoryRow key={s.id} s={s} onToggle={toggle} onDelete={remove} />)}
        {!stories.length && <p className="font-serif italic text-bone-500">No stories yet.</p>}
      </ul>
    </div>
  );
}
